/**
 * 资产-演员管理器
 * 负责管理资产-演员列表
 * @author Joey
 */
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { assetsActorList } from "@/services/assets-actor";
import { actorRename } from "@/services/rename-actor-template";
import { videoUserActorTemplate } from "@/services/video-user-actor-template";
import { canUseWindow } from "@/utils/canuseWindow";
import { videoRetryFailedTemplate } from "@/services/video-retry-failed-template";
import { actorDelete } from "@/services/actor-delete";
import { jsonStore } from "@/manager/cache/localdb";
import { creationActorsManager } from "./creation-actor-manager";
import { agentManager } from "./agent-manager";
import { subscriptionManager } from "./subscription-manager";


/** 继承自 Actor 的资产数字人类型*/
export interface AssetsActorData extends Actor {
  createdAt: string;
  generateStatus: number;
}

// 临时数据
interface TemplateResult {
  id: string,
  generateStatus: number,
  name: string,
  image: string,
  createdAt: string,
  audio: {
    name: string,
  }
}

// 缓存数据结构
interface CacheData {
  actors: AssetsActorData[];
  page: number;
  size: number;
  total: number;
  // 临时数据
  tempResults?: AssetsActorData[];
}

type ActorsChangeEventDetail = {
  actors: AssetsActorData[];
  total: number;
};
type ActorsChangeEvent = CustomEvent<ActorsChangeEventDetail>;

class AssetsActorLocal {
  constructor(private storageKey: string) {}
  async get(): Promise<CacheData | null> {
    return await jsonStore.get<CacheData>(this.storageKey);
  }
  async set(data: CacheData): Promise<void> {
    await jsonStore.set(this.storageKey, data);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AssetsActorRemote {
  constructor(private cache: AssetsActorLocal) {}
  async getCached() {
    return await this.cache.get();
  }
  async fetchActors(page: number, pageSize: number) {
    const data = await assetsActorList({ page, pageSize });
    const actors = data.actors ? data.actors.map(item => ({
      id: item.id,
      modelId: item.modelId,
      name: item.name,
      video: item.fullActorDefaultVideoUrl,
      tags: item.tags,
      image: item.imageUrl,
      isFavorite: item.isFavorite,
      isMyActor: item.isMyActor,
      createdAt: item.createdAt,
      isPro: item.isPro,
      isHD: item.isHD,
      generateStatus: item.generateStatus,
      generateType: item.generateType,
      figureCode: item.figureCode,
      figureMessage: item.figureMessage,
      audio: {
        id: item.defaultAudio?.id,
        voiceTtsId: item.defaultAudio?.voiceTtsId,
        modelName: item.defaultAudio?.modelName,
        name: item.defaultAudio?.name,
        speed: item.defaultAudio?.speed,
        audioUrl: item.defaultAudio?.audioUrl,
        isStarred: item.defaultAudio?.isStarred,
        language: '',
        gender: '',
        ageGroup: '',
      },
      isUnusual: item.isUnusual,
    })) : [];
    const pageNum = data.currentPage;
    const pageSz = data.pageSize;
    const totalNum = data.total - (data.actors?.length - actors?.length);
    await this.cache.set({ actors, page: pageNum, size: pageSz, total: totalNum });
    return { actors, page: pageNum, pageSize: pageSz, total: totalNum };
  }
  async renameActor(id: string, name: string) {
    return actorRename({ id, name });
  }
  async deleteActor(id: string) {
    return actorDelete(id);
  }
  async retryFailed(id: string) {
    return videoRetryFailedTemplate(id);
  }
  async getActorTemplate(id: string) {
    return videoUserActorTemplate(id);
  }
}

export class AssetsActorManager {
  private static instance: AssetsActorManager;
  private readonly STORAGE_KEY = "MIUTA_ASSETS_ACTORS";
  private activeStorageKey = "";
  private actors: AssetsActorData[] = [];
  private tempResults: TemplateResult[] = [];
  private page: number = 1;
  private size: number = 1000;
  private total: number = 0;
  private polling: NodeJS.Timeout | null = null;
  private generatorPollingMap: { [id: string]: NodeJS.Timeout } = {};
  private cache: AssetsActorLocal;
  private api: AssetsActorRemote;

  public constructor() {
    this.cache = new AssetsActorLocal(this.getStorageKey());
    this.api = new AssetsActorRemote(this.cache);
    this.activeStorageKey = this.getStorageKey();
  }

  public static getInstance(): AssetsActorManager {
    if (!AssetsActorManager.instance) {
      AssetsActorManager.instance = new AssetsActorManager();
    }
    return AssetsActorManager.instance;
  }

  public getActors(): AssetsActorData[] {
    return [...(this.tempResults as AssetsActorData[]), ...this.actors]
  }

  private getStorageKey() {
    const agentId = agentManager.getAgentId() || "";
    return `${this.STORAGE_KEY}:${agentId}`;
  }

  private getActorsChangeEventName() {
    return `${this.getStorageKey()}:change`;
  }

  // 当 agent 变化时，自动重建 AssetsActorLocal 和 AssetsActorRemote，避免不同 agent 的缓存串用。
  private ensureStorageScope() {
    const nextStorageKey = this.getStorageKey();
    if (nextStorageKey === this.activeStorageKey) {
      return;
    }
    this.activeStorageKey = nextStorageKey;
    this.cache = new AssetsActorLocal(nextStorageKey);
    this.api = new AssetsActorRemote(this.cache);
  }

  private setActors(actors: AssetsActorData[]) {
    this.ensureStorageScope();
    this.actors = actors;
    this.cache.set({ actors, page: this.page, size: this.size, total: this.total });
    this.dispatchActorsChange();
  }

  public async init() {
    this.ensureStorageScope();
    try {
      const cached = await this.api.getCached();
      if (cached) {
        this.setActors(cached.actors);
        this.page = cached.page;
        this.size = cached.size;
        this.total = cached.total;
        this.fetch();
        return;
      }
    } catch (error) {
      console.error("get cache data failed", error);
      this.setActors([]);
      this.page = 1;
      this.size = 1000;
      this.total = 0;
    }
    await this.fetch();
  }

  // 获取列表数据
  public async fetch() {
    this.ensureStorageScope();
    console.log("fetch data from network");
    try {
      const data = await this.api.fetchActors(this.page, this.size);
      const actors = data.actors;
      this.clearTempResults();
      this.setActors(actors);
      this.page = data.page;
      this.size = data.pageSize;
      this.total = data.total;
      for (const actor of actors) {
        if (actor.generateStatus < 2 && actor.id) {
          this.generatorPolling(actor.id);
        }
      }
    } catch (error) {
      this.dispatchActorsChange();
      console.error("AssetsActorManager fetch error:", error.message);
      throw error;
    }
  }

  // 重新生成数字人
  public async regenerateActor(actor: Actor) {
    const _actors = [...this.actors];
    try {
      this.addTempResults({
        id: actor.id,
        generateStatus: 1,
        name: actor?.name,
        image: actor.image,
        createdAt: actor.createdAt,
        audio: {
          name: actor?.audio?.name,
        }
      });
      // 删除当前点击的数据
      const filteredActors = this.actors.filter(item => item.id !== actor.id);
      this.setActors(filteredActors);

      // 发起重新生成请求
      await this.api.retryFailed(actor.id);
      this.fetch()
    } catch (error) {
      // 回滚
      this.setActors(_actors);
      this.addTempResults(this.tempResults.filter(item => item.id !== actor.id));
      console.error("Failed to regenerate actor:", error);
      throw error
    }
  }

  // ============== 临时数据 ==================
  private addTempResults(results: TemplateResult | TemplateResult[]) {
    this.ensureStorageScope();
    if (Array.isArray(results)) {
      this.tempResults = [...results]
    } else {
      this.tempResults = [results, ...this.tempResults];
    }
    this.cache.set({
      actors: [...(this.tempResults as AssetsActorData[]), ...this.actors],
      page: this.page,
      size: this.size,
      total: this.total
    });
    this.dispatchActorsChange();
  }

  // 清空临时结果
  private clearTempResults() {
    this.tempResults = [];
  }

  // ============== 轮询获取生成结果 ==================

  // 单独轮询 用于获取生成视频结果
  public generatorPolling(id: string): () => void {
    // 已在轮询则清除原轮询函数
    if (this.generatorPollingMap[id]) {
      clearTimeout(this.generatorPollingMap[id]);
    }
    this.generatorPollingMap[id] = setTimeout(async () => {
      try {
        const newResult = await this.api.getActorTemplate(id);
        const existingResults = this.actors || [];
        const resultIndex = existingResults.findIndex(
          (result) => result.id === newResult.id
        );
        if (resultIndex !== -1) {
          // 更新结果列表
          const updatedResults = [...existingResults];
          updatedResults[resultIndex] = {
            ...updatedResults[resultIndex],
            ...newResult,
            image: newResult.imageUrl,
          };
          // 更新数据
          this.setActors(updatedResults);
          // 是否需要继续轮询
          newResult.generateStatus < 2
            ? this.generatorPolling(id)
            : this.stopGeneratorPolling(id);

          return;
        }
      } catch {
        // 发生错误仍需继续轮询
        this.generatorPolling(id);
        return;
      }
      // 在列表中找不到相应的数据则停止不必要的轮询
      this.stopGeneratorPolling(id);
    }, 10000);
    return () => this.stopGeneratorPolling(id);
  }

  // 停止某个单独轮询
  public stopGeneratorPolling(id: string) {
    if (this.generatorPollingMap[id]) {
      clearTimeout(this.generatorPollingMap[id]);
      delete this.generatorPollingMap[id];
    }
  }

  // 开始轮询
  public startPolling(id: string): () => void {
    this.polling = setTimeout(async () => {
      try {
        await this.fetch();
      } catch (error) {
        // 发生错误仍需继续轮询
        this.startPolling(id);
      }
    }, 10000);

    return () => this.stopPolling();
  }

  public stopPolling(): void {
    if (this.polling) {
      clearTimeout(this.polling);
      this.polling = null;
    }
  }

  // 停止所有轮询
  public stopAllPolling() {
    console.log('停止所有轮询')
    this.stopPolling();
    for (const id of Object.keys(this.generatorPollingMap)) {
      this.stopGeneratorPolling(id);
    }
  }
  //================= End ====================

  // 演员重命名
  public async actorRename({ id, name }: { id: string, name: string }) {
    const actorIndex = this.actors.findIndex(actor => actor.id === id);
    const originalName = this.actors[actorIndex].name;
    const originActors = this.actors; // 副本
    try {
      // 乐观更新：立即更新本地状态
      const updatedActors = [...this.actors];
      updatedActors[actorIndex] = {
        ...updatedActors[actorIndex],
        name
      };
      this.setActors(updatedActors);
      // 调用 API 更新数据
      const res = await this.api.renameActor(id, name);
      if (res.isDuplicate) {
        throw new Error("Actor name already exists");
      }
      return res;
    } catch (error) {
      // 失败，回滚到原始状态
      const rolledBackActors = [...originActors];
      rolledBackActors[actorIndex] = {
        ...rolledBackActors[actorIndex],
        name: originalName
      };
      this.setActors(rolledBackActors);
      throw error;
    }
  }

  // 演员删除
  public async actorDelete(id: string) {
    // 乐观更新：立即从本地状态删除
    const actors = [...this.actors];
    const newActors = this.actors.filter((item) => id !== item.id);
    // 数据始终以后端返回为准
    this.setActors(newActors);
    this.dispatchActorsChange();
    try {
      // 调用 API 删除数据
      await this.api.deleteActor(id);
      await this.fetch();
      // 刷新演员选择弹窗的数据
      await creationActorsManager.refresh('my');
    } catch (error) {
      // 失败，回滚到原始状态
      this.setActors(actors);
      this.dispatchActorsChange();
      throw error;
    }
  }

  public getPage(): number {
    return this.page;
  }

  public getSize(): number {
    return this.size;
  }

  public getTotal(): number {
    return this.total;
  }

  // 重置参数到默认值
  public reset(): void {
    this.actors = [];
    this.page = 1;
    this.size = 1000;
    this.dispatchActorsChange();
  }

  /**
   * 清除所有缓存数据
   * 用于退出登录时清理用户数据
   */
  public clear(): void {
    this.ensureStorageScope();
    // 停止所有轮询
    this.stopPolling();
    Object.keys(this.generatorPollingMap).forEach((taskId) => {
      this.stopGeneratorPolling(taskId);
    });
    this.polling = null;
    this.generatorPollingMap = {};
    this.cache.clear();
    console.log("assets cache cleared");
  }

  // 4. 监听函数可监听演员列表变化
  public onActorsChange(listener: (e: ActorsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = this.getActorsChangeEventName();
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }

  // 统一的事件分发方法
  public dispatchActorsChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<ActorsChangeEventDetail>(
        this.getActorsChangeEventName(),
        {
          detail: {
            actors: this.getActors(),
            total: this.total
          },
        }
      );
      window.dispatchEvent(event);
    }
  }
}

export const useAssetsActorData = () => {
  const [actors, setActors] = useState(assetsActorManager.getActors());
  const [total, setTotal] = useState(assetsActorManager.getTotal());
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  
  useVideoCreationPollingCleanup()

  useEffect(() => {
    return assetsActorManager.onActorsChange((e) => {
      setActors(e.detail.actors);
    });
  }, []);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      await assetsActorManager.init();
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  };

  // 获取演员列表
  const fetch = async (loadMore = false) => {
    setLoading(true);
    try {
      await assetsActorManager.fetch();
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  // 演员重命名
  const actorRename = async ({ id, name }: { id: string, name: string }) => {
    try {
      const res = await assetsActorManager.actorRename({ id, name });
      // 重名检查
      if (res.isDuplicate) {
        toast.toast({ description: 'Actor name already exists' });
      }
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    }
  };

  // 加载更多
  const loadMore = async () => {
    if (loading) {
      return;
    }
    if (assetsActorManager.getActors().length >= total) {
      return;
    }
    await fetch(true);
  };

  return {
    actors,
    total,
    loading,
    fetch,
    loadMore,
    actorRename,
  };
};

// 停止轮询的钩子
export const useVideoCreationPollingCleanup = () => {
  useEffect(() => {
    // 返回清理函数，在组件卸载时执行
    return () => {
      // 停止项目轮询
      assetsActorManager.stopAllPolling();
    };
  }, []);
};

// 重新生成
export const useRegenerateActor = () => {
  const toast = useToast();
  const regenerateActor = async (actor: Actor) => {
    try {
      await assetsActorManager.regenerateActor(actor);
      subscriptionManager.fetchAgentCredits();
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to regenerate actor:", error);
      throw error
    }
  }

  return {
    regenerateActor,
  }
}

// 删除演员
export const useDeleteActor = () => {
  const deleteActor = async (id: string) => {
    try {
      await assetsActorManager.actorDelete(id);
    } catch (error) {
      console.error("Failed to delete actor:", error);
      throw error
    }
  }

  return {
    deleteActor
  }
}

export const assetsActorHooks = {
  useData: useAssetsActorData,
  usePollingCleanup: useVideoCreationPollingCleanup,
  useRegenerateActor,
  useDeleteActor,
};

// 导出单例实例
export const assetsActorManager = AssetsActorManager.getInstance();
